// Owner-only alert that lights up red when the Emergent LLM gateway
// is unreachable / out-of-budget / scope-restricted. Pings the backend
// `/owner/llm-health` endpoint once per home visit (the backend itself
// caches the upstream LLM call for 5 minutes so this is essentially
// free). Hides itself for everyone who isn't the owner / admin and
// while the gateway is healthy — so the home page stays clean for the
// 99.9% of normal sessions.
import React, { useEffect, useState } from "react";
import { Warning, ArrowsClockwise, X } from "@phosphor-icons/react";
import api from "../lib/api";

export default function OwnerLlmHealthBanner({ user }) {
  const isOwner = !!(user && (user.is_owner || user.role === "owner" || user.role === "admin"));
  const [state, setState] = useState(null); // {healthy, error, checked_at}
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    api.get("/owner/llm-health").then((r) => setState(r.data)).catch(() => {});
  }, [isOwner]);

  const recheck = async () => {
    setBusy(true);
    try {
      const r = await api.get("/owner/llm-health?force=1");
      setState(r.data);
    } finally {
      setBusy(false);
    }
  };

  if (!isOwner) return null;
  if (!state || state.healthy) return null;
  if (dismissed) return null;

  return (
    <div
      className="mb-6 rounded-2xl border border-red-300 bg-red-50 text-red-900 p-4 md:p-5 flex items-start gap-3"
      data-testid="owner-llm-health-banner"
    >
      <div className="w-9 h-9 rounded-full bg-red-100 grid place-items-center shrink-0">
        <Warning size={18} weight="fill" className="text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-red-700">Owner alert · Emergent LLM key</p>
        <p className="font-heading text-lg md:text-xl text-red-900 mt-1 leading-snug">
          AI features are currently offline
        </p>
        <p className="text-sm text-red-900/85 mt-1">{state.error || "Unknown gateway error."}</p>
        <p className="text-xs text-red-900/75 mt-2">
          Affects: post moderation, "Listen in Modern English", AI devotionals, daily picks, captions.
          Top up at <strong>Profile → Universal Key → Add Balance</strong>.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={recheck}
            disabled={busy}
            data-testid="owner-llm-health-recheck"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-60"
          >
            <ArrowsClockwise size={12} weight="bold" className={busy ? "animate-spin" : ""} />
            {busy ? "Re-checking…" : "Re-check now"}
          </button>
          <a
            href="/settings"
            data-testid="owner-llm-health-settings"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-white border border-red-300 text-red-800 text-xs font-semibold hover:bg-red-100"
          >
            Open diagnostics
          </a>
        </div>
      </div>
      <button
        onClick={() => setDismissed(true)}
        data-testid="owner-llm-health-dismiss"
        className="w-8 h-8 grid place-items-center rounded-full hover:bg-red-100 text-red-700"
        aria-label="Dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
